import { Injectable } from '@angular/core';
import { sp, ISiteGroupInfo } from '@pnp/sp/presets/all';
import { ISiteUserInfo } from '@pnp/sp/site-users/types';
import { execute } from './utils';
import "@pnp/sp/profiles";


@Injectable({
  providedIn: 'root'
})
export class CurrentUserService {

  private currentUserPromise: Promise<ISiteUserInfo>;
  private groupsPromise: Promise<ISiteGroupInfo[]>;
  private usersPromise: Promise<ISiteUserInfo[]>;
  private properties: Promise<any[]>;


  get(): Promise<ISiteUserInfo> {
    if (!this.currentUserPromise)
    {
      this.currentUserPromise = this.getCurrentUserImplementation();
      this.currentUserPromise.catch(error => {
        this.currentUserPromise = undefined;
        console.error('Failed to get current user %o', error);
      });
    }
    return this.currentUserPromise;
  }

  private async getCurrentUserImplementation(): Promise<ISiteUserInfo> {
    const timeZonePromise = sp.web.regionalSettings.timeZone.get();
    /*
    const timeZonesPromise = sp.web.regionalSettings.timeZones.get();
    timeZonesPromise.then(timeZones => {
      console.log(timeZones);
    });
    */
    const user = await execute(() => sp.web.currentUser.get());

    user.IsAccessHubMember = await this.isAccesshubMember(user.Id);
    user.TimeZone = await timeZonePromise;
    return user;
  }

  public async getCurrentUserID(): Promise<any> {
    const user = await execute(() => sp.web.currentUser.get());
    return user.Id;
  }

  public async getCurrentUserEmail(): Promise<any> {
    const user = await execute(() => sp.web.currentUser.get());
    return user.Email;
  }

  public async isAccesshubMember(userId: number) : Promise<boolean> {
    const configuredGroups = await this.getConfiguredGroupList();
    const userGroups = await sp.web.siteUsers.getById(userId).groups();
    if (configuredGroups.length === 0) {
      return false;
    } else {
      const isMemberResult = this.isMember(configuredGroups, userGroups);
      return isMemberResult;
    }
  }

  public isMember(configuredroupList: any, userGroupList: any): boolean  {
    let result = false;
    for (let i = 0; i < userGroupList.length; i++) {
      if (configuredroupList.includes(userGroupList[i].Title)) {
        result = true;
        break;
      }
    }
    return result;
  }

  public async getConfiguredGroupList(): Promise<any> {
    return new Promise((resolve) => {
      let configItem = undefined;
      sp.web.lists
        .getByTitle("HTAConstantsConfig")
        .items.get()
        .then((result) => {
          configItem = result.filter(function (record) {
            return record.Title == "MembershipGroupList";
          });
          return resolve(
            configItem === undefined || configItem.length === 0
              ? []
              : configItem[0].ConstValue
          );
        });
    });
  }

  async getProperties(): Promise<any[]> {
    return sp.profiles.myProperties.get();
  }

  getGroups(): Promise<ISiteGroupInfo[]> {
    if (!this.groupsPromise) {
      this.groupsPromise  = execute(() => sp.web.currentUser.groups.get());
      this.groupsPromise.catch(error => {
        this.groupsPromise = undefined;
        console.error('Failed to get current user groups. %o', error);
      });
    }
    return this.groupsPromise;
  }

  getUsers() : Promise<ISiteUserInfo[]>{
    if(!this.usersPromise){
      this.usersPromise = execute(() => sp.web.siteUsers.get());
      this.usersPromise.catch(error => {
        this.usersPromise = undefined;
        console.error('Failed to get site users. %o', error);
      })
    }
    return this.usersPromise;
  }

  async isCurrentUserSiteOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'AccessHub Owners');
  }

  async isCurrentUserSiteMember(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'AccessHub Members');
  }

  async isCurrentUserSiteVisitor(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'AccessHub Visitors');
  }

  async isCurrentUserESAOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'ESAOwners');
  }

  async isCurrentUserESACreator(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'ESACreators');
  }

  async isCurrentUserTPOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'TP1TP2Owners');
  }

  async isCurrentUserTPCreator(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'TP1TP2Creators');
  }

  async isCurrentUserEMCCOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'HTAEMCCOwners');
  }

  async isCurrentUserEMCCCreator(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'HTAEMCCCreators');
  }

  async isCurrentUserHTAPlatformOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'HTA Platform Owners');
  }

  async isCurrentUserAcChannelOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'ACChannelOwners');
  }

  async isCurrentUserEepChannelOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'EEPOwners');
  }

  async isCurrentUserEepChannelCreator(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'EEPCreators');
  }
  async isCurrentUserEepChannelProgramCoordinator(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'EEPProgramCoordinators');
  }
  async isCurrentUserAemChannelOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'AEMOwners');
  }
  
  async isCurrentUserAemCreator(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'AEMCreators');
  }
  async isCurrentUserEvidenceTrackerAdmin(): Promise<boolean> {
    const userGroups = await this.getGroups();

    return userGroups.some(x => x.LoginName === 'MAAP Evidence Tracker Admin');
  }
  async isCurrentUserContextOwner(context: string): Promise<boolean> {
    if(!context || context === undefined || context.length === 0) return false;

    const userGroups = await this.getGroups();

    const regExp = new RegExp(`AC${context}CommunityOwner`);

    return userGroups.some(x => regExp.test(x.LoginName));
  }

  async isCurrentUserCommunityContextOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();

    const regExp = new RegExp(/AC[A-Za-z]*CommunityOwners/gm);

    return userGroups.some(x => regExp.test(x.LoginName));
  }

  async isCurrentUserContextMember(context: string): Promise<boolean> {
    if(!context || context === undefined || context.length === 0) return false;

    const userGroups = await this.getGroups();

    const regExp = new RegExp(`AC${context}CommunityMember`);

    return userGroups.some(x => regExp.test(x.LoginName));
  }

  async isCurrentUserCommunityContextMember(): Promise<boolean> {
    const userGroups = await this.getGroups();

    const regExp = new RegExp(/AC[A-Za-z]*CommunityMember/gm);

    return userGroups.some(x => regExp.test(x.LoginName));
  }

  async isCurrentUserACHTACommunityOwners(): Promise<boolean> {
    const userGroups = await this.getGroups();
    return userGroups.some(x => x.LoginName === 'ACHTACommunityOwners');
  }

  async isCurrentUserPRSROwner(): Promise<boolean> {
    const userGroups = await this.getGroups();
    return userGroups.some(x => x.LoginName === 'HSROwners');
  }

  async isCurrentUserPRSRCreator(): Promise<boolean> {
    const userGroups = await this.getGroups();
    return userGroups.some(x => x.LoginName === 'HSRCreators');
  }

  async isCurrentUserMaapOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();
    return userGroups.some(x => x.LoginName === 'MAAPOwners');
  }

  async isCurrentUserMaapCreator(): Promise<boolean> {
    const userGroups = await this.getGroups();
    return userGroups.some(x => x.LoginName === 'MAAPCreators');
  }

  async isCurrentUserHTATrendsOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();
    return userGroups.some(x => x.LoginName === 'HTATOwners');
  }

  async isCurrentUserHTATrendsCreator(): Promise<boolean> {
    const userGroups = await this.getGroups();
    return userGroups.some(x => x.LoginName === 'HTATCreators');
  }

  async isCurrentUserTimelineOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();
    return userGroups.some(x => x.LoginName === 'TimelineOwners');
  }

  async isCurrentUserTimelineCreator(): Promise<boolean> {
    const userGroups = await this.getGroups();
    return userGroups.some(x => x.LoginName === 'TimelineCreators');
  }

  async isCurrentUserIsCollaborator(additionalUsersId: number[] = []): Promise<boolean> {
    if (!additionalUsersId || additionalUsersId.length == 0) {
      return false;
    }
    const currentUser = await this.get();
    const isCollaborator = additionalUsersId.filter(x => x === currentUser.Id);
    if (isCollaborator.length > 0) {
      return true;
    }
    return false;
  }

  async isCurrentUserFAQChannelOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();    
    return userGroups.some(x => x.LoginName === 'FAQOwners');    
  }

  async isCurrentUserBrandsTrackerAdminBotox(): Promise<boolean> {
    const userGroups = await this.getGroups();            
    return userGroups.some(x => x.LoginName === 'Brands Tracker Admin Botox');    
  }

  async isCurrentUserContractRightsOwner(): Promise<boolean> {	
    const userGroups = await this.getGroups();	
    return userGroups.some(x => x.LoginName === 'ContractRightsOwners');	
    	
  }
  async isCurrentUserCRPricingOwners(): Promise<boolean> {
    const userGroups = await this.getGroups();
    return userGroups.some(x => x.LoginName === 'ContractRightsPricingOwners');
  }
  async isCurrentUserAERCOwner(): Promise<boolean> {
    const userGroups = await this.getGroups();
    return userGroups.some(x => x.LoginName === 'AERCOwners');
  }
  async isCurrentUserAERCCreators(): Promise<boolean> {
    const userGroups = await this.getGroups();
    return userGroups.some(x => x.LoginName === 'AERCCreators');
  }
}
